import type { ApiResponse, ApiError } from '@roi365/shared'

const API_HOST = import.meta.env.VITE_API_URL ?? ''
const BASE_URL = `${API_HOST}/api`

// SEC (H-01): los tokens de sesión viven exclusivamente en cookies
// HttpOnly+Secure+SameSite=Strict emitidas por la API. El JS de la SPA
// (incluido cualquier XSS) no puede leerlos. La única clave en
// localStorage es la del tenant que el super_admin está impersonando —
// no es secreto y se envía como X-Tenant-Id.
const TENANT_KEY = 'roi365_active_tenant'

/**
 * Cabeceras opcionales para fetch directos: incluye X-Tenant-Id si el
 * super_admin tiene un tenant impersonado. NO incluye Authorization —
 * la cookie de sesión va sola con credentials: 'include'.
 */
export function getAuthHeaders(): Record<string, string> {
  const tenantOverride = localStorage.getItem(TENANT_KEY)
  return tenantOverride ? { 'X-Tenant-Id': tenantOverride } : {}
}

// Single-flight: si varias peticiones reciben 401 a la vez, comparten un único
// POST /auth/refresh. El refresh token rota en cada uso, así que dos refresh en
// paralelo se pisarían (el segundo llegaría con el token ya rotado) y podrían
// invalidar la sesión.
let refreshInFlight: Promise<boolean> | null = null

function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

async function doRefresh(): Promise<boolean> {
  try {
    // La cookie refresh_token (path=/api/auth) viaja sola con credentials.
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })

    if (!res.ok) {
      // Distingue idle timeout para mostrar el motivo en la pantalla de login.
      try {
        const errJson = (await res.json()) as ApiError
        if (errJson?.error?.code === 'idle_timeout') {
          sessionStorage.setItem('logout_reason', 'idle')
        }
      } catch {
        // body no parseable — ignorar
      }
      return false
    }

    // El refresh emite cookies nuevas (access + refresh rotado). El body
    // solo contiene { user } — no necesitamos hacer nada con él aquí.
    return true
  } catch {
    return false
  }
}

/**
 * Fetch directo a la API (para endpoints que devuelven blobs, CSV o paginación
 * cruda). Maneja el 401 igual que ApiClient: refresca la sesión (single-flight)
 * y reintenta una vez; si el refresh falla, redirige a /login.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const doFetch = () =>
    fetch(`${BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...getAuthHeaders(),
        ...(init.headers as Record<string, string> | undefined),
      },
    })

  let res = await doFetch()

  if (res.status === 401 && !path.startsWith('/auth/')) {
    const refreshed = await refreshSession()
    if (refreshed) {
      res = await doFetch()
    } else if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
  }

  return res
}

/**
 * apiFetch + parseo JSON con el envelope completo ({ ok, data, meta }).
 * Lanza ApiRequestError si la respuesta no es 2xx — imprescindible en queryFns
 * de react-query: si la query "resuelve" con un body de error, react-query
 * sobreescribe los datos buenos de la caché y la UI se queda vacía.
 */
export async function apiFetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init)
  const json = await res.json()
  if (!res.ok) {
    const error = json as ApiError
    throw new ApiRequestError(
      error.error?.message ?? 'Request failed',
      error.error?.code ?? 'UNKNOWN',
      res.status,
      error.error?.details,
    )
  }
  return json as T
}

class ApiClient {
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(init?.headers as Record<string, string>),
    }

    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers,
    })

    // Handle 401 — try refresh
    if (res.status === 401 && path !== '/auth/refresh' && path !== '/auth/login') {
      const refreshed = await this.tryRefresh()
      if (refreshed) {
        // Retry — la nueva cookie de access_token ya está en el jar.
        const retryRes = await fetch(`${BASE_URL}${path}`, {
          ...init,
          credentials: 'include',
          headers,
        })
        const retryJson = await retryRes.json()
        if (!retryRes.ok) {
          const error = retryJson as ApiError
          throw new ApiRequestError(
            error.error?.message ?? 'Request failed',
            error.error?.code ?? 'UNKNOWN',
            retryRes.status,
            error.error?.details,
          )
        }
        return (retryJson as ApiResponse<T>).data
      }
      // Refresh falló — la sesión se acabó. La cookie ya está expirada
      // o se ha invalidado; redirigimos a /login.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
      throw new ApiRequestError('Session expired', 'SESSION_EXPIRED', 401)
    }

    const json = await res.json()

    if (!res.ok) {
      const error = json as ApiError
      throw new ApiRequestError(
        error.error?.message ?? 'Request failed',
        error.error?.code ?? 'UNKNOWN',
        res.status,
        error.error?.details,
      )
    }

    return (json as ApiResponse<T>).data
  }

  private tryRefresh(): Promise<boolean> {
    return refreshSession()
  }

  get<T>(path: string) {
    return this.request<T>(path)
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  delete<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    })
  }
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: Record<string, unknown>,
  ) {
    // Include validation field errors in the message if present
    let fullMessage = message
    if (details?.fields && typeof details.fields === 'object') {
      const fields = details.fields as Record<string, string[] | undefined>
      const fieldErrors = Object.entries(fields)
        .filter(([, v]) => Array.isArray(v) && v.length > 0)
        .map(([k, v]) => `${k}: ${(v as string[]).join(', ')}`)
        .join(' | ')
      if (fieldErrors) fullMessage = `${message} — ${fieldErrors}`
    }
    super(fullMessage)
    this.name = 'ApiRequestError'
  }
}

export const api = new ApiClient()
